import React, { useEffect, useRef } from "react";
import useOverlayScrollLock from "@/hooks/useOverlayScrollLock";

/**
 * Robust modal overlay that only closes when the pointer is pressed
 * and released on the backdrop itself.
 *
 * Prevents accidental closes when the user:
 *   - selects text inside the modal and releases the mouse outside the panel
 *   - pastes via context menu / keyboard shortcuts
 *   - clicks scrollbars or form controls inside the modal
 */
export function ModalOverlay({ onClose, children, className = "", style = {}, allowEscape = true }) {
  const pointerDownOnOverlay = useRef(false);
  useOverlayScrollLock();

  useEffect(() => {
    if (!allowEscape) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, allowEscape]);

  return (
    <div
      className={`fixed inset-0 z-50 flex ${className}`}
      style={{ overflowY: "auto", overscrollBehaviorY: "contain", ...style }}
      onPointerDown={(e) => {
        pointerDownOnOverlay.current = e.target === e.currentTarget;
      }}
      onPointerUp={(e) => {
        if (pointerDownOnOverlay.current && e.target === e.currentTarget) {
          onClose?.();
        }
        pointerDownOnOverlay.current = false;
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

/**
 * Popover helper: attach an outside-pointer-down listener that only fires
 * when the pointer starts outside the supplied ref element.
 */
export function useOutsideClick(ref, onClose) {
  useEffect(() => {
    if (!ref.current || !onClose) return;
    const handlePointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [ref, onClose]);
}
